import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { ProjectMembership } from "@medplum/fhirtypes";
import { useMedplumContext } from "@medplum/react-hooks";
import { captureException } from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Membership, Project } from "@/types/medplum";
import {
  ANALYTICS_EVENTS,
  ANALYTICS_GROUPS,
} from "@/constants/analytics.constants";
import { medplumClient } from "@/lib/medplum";
import { posthog } from "@/lib/posthog";
import { getReferenceId } from "@/lib/utils/helpers";
import {
  activeProjectCache,
  membershipsCache,
} from "@/lib/utils/helpers/auth-cache";
import { useUser } from "@/context/user-provider";

import { projectQueries } from "@/queries/project.queries";

type ProjectProviderProps = {
  children: ReactNode;
};

type State = {
  projectList: Project[];
  project: Project | null;
  isSuperAdmin: boolean;
  selectProject: (project: Project) => Promise<void>;
  refresh: () => Promise<void>;
  loading: boolean;
};

const initialState: State = {
  projectList: [],
  project: null,
  isSuperAdmin: false,
  selectProject: () => Promise.resolve(),
  refresh: () => Promise.resolve(),
  loading: false,
};

const ProjectProviderContext = createContext<State>(initialState);

const toMembership = (
  projectMembership: ProjectMembership
): Membership | null => {
  const projectId = getReferenceId(projectMembership.project.reference);

  if (!projectId || !projectMembership.id) {
    return null;
  }

  return {
    id: projectMembership.id,
    project: {
      id: projectId,
      name: projectMembership.project.display ?? projectId,
    },
  };
};

const getActiveProject = (): Project | null => {
  const login = medplumClient.getActiveLogin();

  if (!login) {
    return null;
  }

  const id = getReferenceId(login.project.reference);

  if (!id) {
    return null;
  }

  return { id, name: login.project.display ?? id };
};

export const ProjectProvider: React.FC<ProjectProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { profile } = useMedplumContext();
  const { userLoading } = useUser();
  const queryClient = useQueryClient();
  const [superAdminProjectRevision, setActingAsRevision] = useState(0);

  const memberships = useMemo<Membership[]>(() => {
    return membershipsCache
      .get()
      .map(toMembership)
      .filter((membership): membership is Membership => membership !== null);
    /**
     * profile dependency re-derives the list after login switches the active login
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const selectedProject = useMemo<Project | null>(() => {
    const cachedProjectId = activeProjectCache.get();

    if (cachedProjectId) {
      const membership = memberships.find(
        ({ project: { id: projectId } }) => projectId === cachedProjectId
      );

      if (membership) {
        return membership.project;
      }
    }

    return getActiveProject();
    /**
     * profile dependency re-derives after login swaps active session
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, memberships]);

  const isSuperAdmin = useMemo(() => {
    return medplumClient.isSuperAdmin();
    /**
     * profile dependency re-derives after login swaps active session
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    ...projectQueries.superAdminProjects(profile?.id),
    enabled: isSuperAdmin,
  });

  const superAdminProject = useMemo<Project | null>(() => {
    if (!isSuperAdmin) {
      return null;
    }

    const id = activeProjectCache.get();

    if (id) {
      const found = projects.find((project) => project.id === id);

      if (found) {
        return found;
      }
    }

    return projects[0] ?? null;
    /**
     * superAdminProjectRevision triggers re-derivation after setValue without React state mirror
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, projects, superAdminProjectRevision]);

  useEffect(() => {
    if (!superAdminProject) {
      return;
    }

    const cachedId = activeProjectCache.get();

    if (cachedId === superAdminProject.id) {
      return;
    }

    activeProjectCache.set(superAdminProject.id);
    void queryClient.invalidateQueries();
  }, [superAdminProject, queryClient]);

  const project = isSuperAdmin ? superAdminProject : selectedProject;

  useEffect(() => {
    if (!project?.id) {
      return;
    }

    posthog.group(ANALYTICS_GROUPS.PROJECT, project.id, {
      name: project.name,
    });
  }, [project?.id, project?.name]);

  const projectList = isSuperAdmin
    ? projects
    : memberships.map((membership) => membership.project);

  const setSuperAdminProject = useCallback(
    async ({ id }: Project) => {
      activeProjectCache.set(id);
      setActingAsRevision((revision) => revision + 1);

      /**
       * Invalidation rather than a clear: a super admin switching projects
       * keeps the same login, so nothing cached belongs to another user.
       */
      await queryClient.invalidateQueries();
    },
    [queryClient]
  );

  const setMembership = useCallback(
    async ({ id, name }: Project) => {
      const logins = medplumClient.getLogins();
      const login = logins.find(
        ({ project: { reference } }) => getReferenceId(reference) === id
      );

      if (!login) {
        captureException(new Error("No stored login found for project"), {
          extra: { projectId: id },
        });
        toast.error(t("Organization is not available"));
        return;
      }

      activeProjectCache.set(id);

      /**
       * Clear before the switch, not after it: every cached query belongs to
       * the login being left behind. Invalidating would keep that data on
       * screen until the refetch lands under the new login — or for good, if
       * the new login has less access and the refetch fails.
       */
      queryClient.clear();

      await medplumClient.setActiveLogin(login);

      const membershipId =
        memberships.find((membership) => membership.project.id === id)?.id ??
        id;

      posthog.capture(ANALYTICS_EVENTS.MEMBERSHIP_SWITCHED, {
        membershipId,
        projectName: name,
      });
    },
    [queryClient, t, memberships]
  );

  const selectProject = useCallback(
    async (nextProject: Project) => {
      if (isSuperAdmin) {
        await setSuperAdminProject(nextProject);
      } else {
        await setMembership(nextProject);
      }
    },
    [isSuperAdmin, setSuperAdminProject, setMembership]
  );

  const refresh = useCallback(async () => {
    if (isSuperAdmin) {
      await queryClient.invalidateQueries({
        queryKey: projectQueries.allSuperAdminProjects(),
      });

      return;
    }

    const activeLogin = medplumClient.getActiveLogin();
    const id = getReferenceId(activeLogin?.project.reference);

    if (!activeLogin || !id) {
      return;
    }

    try {
      const fetched = await medplumClient.readResource("Project", id);

      if (!fetched.name) {
        return;
      }

      await medplumClient.setActiveLogin({
        ...activeLogin,
        project: { ...activeLogin.project, display: fetched.name },
      });

      const stored = membershipsCache.get();

      if (stored.length) {
        const updated = stored.map((projectMembership) =>
          projectMembership.project.reference?.endsWith(`/${id}`)
            ? {
                ...projectMembership,
                project: {
                  ...projectMembership.project,
                  display: fetched.name,
                },
              }
            : projectMembership
        );
        membershipsCache.set(updated);
      }
    } catch {
      /**
       * Ignore refresh failures — stale name doesn't affect functionality
       */
    }
  }, [isSuperAdmin, queryClient]);

  const value = useMemo<State>(
    () => ({
      projectList,
      project,
      isSuperAdmin,
      selectProject,
      refresh,
      loading: userLoading || (isSuperAdmin && projectsLoading),
    }),
    [
      projectList,
      project,
      isSuperAdmin,
      selectProject,
      refresh,
      userLoading,
      projectsLoading,
    ]
  );

  return (
    <ProjectProviderContext.Provider value={value}>
      {children}
    </ProjectProviderContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectProviderContext);

  if (context === undefined) {
    throw new Error("useProject must be used within a ProjectProvider");
  }

  return context;
};
